// React and Hooks
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";

// AG Grid Components
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeBalham,
} from "ag-grid-community";

// Material-UI Components
import {
  Box,
  Button,
  Grid,
  Chip,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  LinearProgress,
  Typography,
} from "@mui/material";
import { Add as AddIcon, MoreVert as MoreVertIcon } from "@mui/icons-material";

// Date Utility
import dayjs from "dayjs";

// Local Components
import LGModal from "../../molecules/LGModal";
import CPOForm from "./CPOForm";
import CPOShipments from "./CPOShipments";
import CPOOperationStatusModal from "./CPOOperationStatusModal";
import {
  UpdateDropdownEditor,
  TextCellRenderer,
  NumberCellRenderer,
  DateCellRenderer,
  BooleanCellRenderer,
  DropdownCellRenderer,
} from "../../atoms/Inputs/index.ts";

// API Services
import {
  deleteClientPurchaseOrder,
  getClientPurchaseOrders,
  patchClientPurchaseOrder,
} from "../../api/clientPurchaseOrders";
import { getCatalogue, getStatusCatalogue } from "../../api/common/catalogues.ts";

// Utility Services
import { useSnack } from "../../services/SnakbarProvider.js";
import { handleError } from "../../services/errorHandler.js";
import { getUserId, getUserRoles } from "../../services/auth.js";
import { useAgGridPersistedPagination } from "../../hooks/usePersistedPagination";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

const ClientPurchaseOrderCrud = ({ companyId }) => {
  const { openSnack } = useSnack();
  const gridRef = useRef();
  const { paginationPageSize, onPaginationChanged, restorePage } =
    useAgGridPersistedPagination();

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCPO, setSelectedCPO] = useState(null);
  const [shipmentsModalOpen, setShipmentsModalOpen] = useState(false);
  const [operationStatusModalOpen, setOperationStatusModalOpen] = useState(false);
  const [statusCatalog, setStatusCatalog] = useState([]);
  const isSuperadmin = getUserRoles().roles.includes("Superadmin");

  const setStatusColor = (statusCode) => {
    switch (statusCode) {
      case "CPO_CREATED":
        return "warning";
      case "CPO_APPROVED":
        return "default";
      case "CPO_PRODUCED":
        return "info";
      case "CPO_DELIVERED":
      case "CPO_CHARGED":
        return "success";
      case "CPO_CANCELLED":
        return "error";
    }
  };

  const handleChange = () => {
    setShowCompleted(!showCompleted);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const userId = getUserId();
      const response = await getClientPurchaseOrders({
        companyId,
        showCompleted,
        pageSize: 1000,
        // TODO: Descomentar cuando se tenga bien definida la regla de negocio para el vendedor
        // sellerId: userId,
      });
      setRowData(response.data || []);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusCatalog = async () => {
    try {
      const response = await getStatusCatalogue("CLIENT_PO");
      setStatusCatalog(response.data || []);
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  useEffect(() => {
    fetchData();
    fetchStatusCatalog();
  }, [companyId, showCompleted]);

  const onGridReady = useCallback((params) => {
    fetchData();
  }, []);

  const onFirstDataRendered = useCallback((params) => {
    params.api.autoSizeAllColumns();
    restorePage(params.api);
  }, [restorePage]);

  const handleDelete = async (id) => {
    try {
      await deleteClientPurchaseOrder(id);
      fetchData();
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const handleViewShipments = (data) => {
    console.log(data);
    setSelectedCPO(data);
    setShipmentsModalOpen(true);
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    fetchData();
  };

  const handleEdit = (data) => {
    setSelectedCPO(data);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedCPO(null);
    fetchData();
  };

  const handleShipmentsModalClose = () => {
    setShipmentsModalOpen(false);
    setSelectedCPO(null);
    fetchData();
  };

  const handleViewOperationStatus = (data) => {
    setSelectedCPO(data);
    setOperationStatusModalOpen(true);
  };

  const handleOperationStatusModalClose = () => {
    setOperationStatusModalOpen(false);
    setSelectedCPO(null);
  };

  const handleCellValueChanged = async (params) => {
    // Skip if this is a status change since it's handled by UpdateDropdownEditor
    if (params.column.colId === 'statusId') return;

    const col = params.column.colId;
    const value = params.value;
    const id = params.data.id;

    try {
      await patchClientPurchaseOrder(id, { [col]: value });
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const handleCellEditingStopped = async (params) => {
    // Skip if this is a status change since it's handled by UpdateDropdownEditor
    if (params.column.colId === 'statusId') return;

    // Only proceed if the value actually changed
    if (params.value !== params.oldValue) {
      const col = params.column.colId;
      const value = params.value;
      const id = params.data.id;

      try {
        await patchClientPurchaseOrder(id, { [col]: value });
      } catch (error) {
        openSnack(handleError(error), "error");
      }
    }
  };

  const ActionsCell = (params) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <Stack direction="row" spacing={0.5}>
        <IconButton size="small" onClick={handleClick} color="primary">
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "more-button",
          }}
        >
          <MenuItem
            onClick={() => {
              params.context.onEdit?.(params.data);
              handleClose();
            }}
          >
            Editar
          </MenuItem>
          <MenuItem
            onClick={() => {
              params.context.onDelete(params.data.id);
              handleClose();
            }}
          >
            Eliminar
          </MenuItem>
          <MenuItem
            onClick={() => {
              params.context.onViewShipments?.(params.data);
              handleClose();
            }}
          >
            Envíos
          </MenuItem>
          {params.context.isSuperadmin && (
            <MenuItem
              onClick={() => {
                params.context.onViewOperationStatus?.(params.data);
                handleClose();
              }}
            >
              Proceso de Operación
            </MenuItem>
          )}
        </Menu>
      </Stack>
    );
  };

  const components = useMemo(
    () => ({
      TextCellRenderer: TextCellRenderer,
      NumberCellRenderer: NumberCellRenderer,
      DateCellRenderer: DateCellRenderer,
      BooleanCellRenderer: BooleanCellRenderer,
      DropdownCellRenderer: DropdownCellRenderer,
      UpdateDropdownEditor: UpdateDropdownEditor,
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      // Fixed columns to the left
      {
        field: "client.name",
        headerName: "Cliente",
        flex: 1,
        pinned: "left",
        minWidth: 120,
      },
      {
        field: "code",
        headerName: "PO",
        flex: 1,
        editable: true,
        pinned: "left",
        minWidth: 120,
      },
      {
        field: "details",
        headerName: "Productos",
        flex: 1.5,
        pinned: "left",
        minWidth: 160,
        valueGetter: (params) => {
          const details = params.data?.details || [];
          return details
            .map((d) => d.finishedGood?.code || d.finishedGoodId)
            .filter(Boolean)
            .join(", ");
        },
        cellRenderer: (params) => {
          const details = params.data?.details || [];
          if (details.length === 0) return "-";
          return (
            <Stack spacing={0.5} sx={{ py: 0.5 }}>
              {details.map((d) => {
                const code = d.finishedGood?.code || d.finishedGoodId;
                if (!code) return null;
                return (
                  <Typography
                    key={d.id ?? code}
                    variant="body2"
                    component="div"
                    sx={{ lineHeight: "28px", minHeight: 28 }}
                  >
                    {code}
                  </Typography>
                );
              })}
            </Stack>
          );
        },
      },
      {
        field: "statusId",
        headerName: "Estado",
        flex: 1,
        pinned: "left",
        editable: true,
        cellRenderer: (params) => (
          <Chip
            label={(params.data.status?.name).toUpperCase() || "-"}
            color={setStatusColor(params.data.status?.code)}
            size="small"
          />
        ),
        cellEditor: "UpdateDropdownEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          options: statusCatalog || [],
          valueField: "id",
          displayField: "name",
          updateFunction: patchClientPurchaseOrder,
        },
      },
      {
        field: "workOrder",
        headerName: "Orden de Trabajo",
        flex: 1,
        editable: false,
        minWidth: 140,
        valueGetter: (params) => {
          const details = params.data?.details || [];
          return details
            .map((d) => d.workOrder)
            .filter(Boolean)
            .join(", ");
        },
        cellRenderer: (params) => {
          const details = params.data?.details || [];
          if (details.length === 0) return "-";
          return (
            <Stack spacing={0.5} sx={{ py: 0.5 }}>
              {details.map((d) => (
                <Typography
                  key={d.id ?? `${d.finishedGoodId}-${d.workOrder}`}
                  variant="body2"
                  component="div"
                  sx={{ lineHeight: "28px", minHeight: 28 }}
                >
                  {d.workOrder || "-"}
                </Typography>
              ))}
            </Stack>
          );
        },
      },
      {
        field: "totalQuantity",
        headerName: "Cantidad",
        flex: 1,
        editable: false,
        cellRenderer: (params) => {
          if (params.node.isRowPinned()) return params.value;

          const details = params.data?.details || [];
          if (details.length === 0) {
            const shippedQuantity = params.data.totalShippedQuantity || 0;
            const totalQuantity = params.data.totalQuantity || 0;
            return `${shippedQuantity} / ${totalQuantity}`;
          }

          return (
            <Stack spacing={0.5} sx={{ py: 0.5, width: "100%", minWidth: 100 }}>
              {details.map((d) => {
                const shippedQuantity = d.shippedQuantity || 0;
                const totalQuantity = d.quantity || 0;
                const progress =
                  totalQuantity > 0
                    ? (shippedQuantity / totalQuantity) * 100
                    : 0;
                return (
                  <Box
                    key={d.id ?? `${d.finishedGoodId}-${totalQuantity}`}
                    sx={{ minHeight: 28, display: "flex", flexDirection: "column", justifyContent: "center" }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                      {`${shippedQuantity} / ${totalQuantity}`}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 8,
                        borderRadius: 2,
                        bgcolor: "grey.200",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          );
        },
        cellStyle: { padding: "8px" },
      },

      {
        field: "receptionDate",
        headerName: "Fecha de Recepción",
        flex: 1,
        editable: true,
        cellEditor: "agDateStringCellEditor",
        valueFormatter: (params) =>
          params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-",
      },
      {
        field: "requestedDeliveryDate",
        headerName: "Fecha de Entrega Solicitada",
        flex: 1,
        editable: true,
        cellEditor: "agDateStringCellEditor",
        valueFormatter: (params) =>
          params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-",
      },
      {
        field: "firstMad",
        headerName: "First MAD",
        flex: 1,
        editable: true,
        cellEditor: "agDateStringCellEditor",
        valueFormatter: (params) =>
          params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-",
      },
      {
        field: "lastMad",
        headerName: "Last MAD",
        flex: 1,
        editable: true,
        cellEditor: "agDateStringCellEditor",
        valueFormatter: (params) =>
          params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-",
      },
      {
        field: "daysUntilMad",
        headerName: "Days until MAD",
        flex: 1,
        cellRenderer: (params) => {
          const lastMad = dayjs(params.data.lastMad);
          const today = dayjs();
          const diffDays = lastMad.diff(today, "d");
          let chipColor = "success";

          if (diffDays < 0) {
            chipColor = "error";
          } else if (diffDays <= 5) {
            chipColor = "warning";
          }

          return (
            <Chip label={`${diffDays} días`} color={chipColor} size="small" />
          );
        },
      },
      {
        field: "rfid",
        headerName: "RFID",
        flex: 0.5,
        editable: true,
        cellEditor: "agCheckboxCellEditor",
      },
      {
        field: "isAGS",
        headerName: "AGS",
        flex: 0.5,
        editable: true,
        cellEditor: "agCheckboxCellEditor",
      },
      // Fixed actions column to the right
      {
        field: "actions",
        headerName: "Acciones",
        sortable: false,
        filter: false,
        pinned: "right",
        maxWidth: 100,
        cellRenderer: ActionsCell,
      },
    ],
    [statusCatalog] // Added statusCatalog as a dependency
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      autoHeight: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      headerClass: "wrap-text-header",
      minWidth: 90,
    }),
    []
  );

  // Add custom CSS for header wrapping
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .ag-cell {
        min-width: 90px !important;
      }
      .wrap-text-header .ag-header-cell-label {
        white-space: normal !important;
        line-height: 1.2;
      }
      .wrap-text-header .ag-header-cell-label span {
        white-space: normal !important;
        word-break: break-word;
      }
      .wrap-text-header .ag-header-cell {
        padding: 4px !important;
      }
      .ag-header-cell {
        line-height: 1.2;
      }
      .ag-header-cell-text {
        overflow: visible !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <Box width="100%">
      <LGModal
        open={addModalOpen}
        handleClose={() => setAddModalOpen(false)}
        header="Agregar CPO"
      >
        <CPOForm onSubmit={handleAddModalClose} companyId={companyId} />
      </LGModal>

      <LGModal
        open={editModalOpen}
        handleClose={() => {
          setEditModalOpen(false);
          setSelectedCPO(null);
        }}
        header="Editar CPO"
      >
        {editModalOpen && selectedCPO && (
          <CPOForm
            id={selectedCPO.id}
            onSubmit={handleEditModalClose}
            companyId={companyId}
          />
        )}
      </LGModal>

      <LGModal
        open={shipmentsModalOpen}
        handleClose={handleShipmentsModalClose}
        header="Envíos"
      >
        <CPOShipments props={selectedCPO} />
      </LGModal>

      <LGModal
        open={operationStatusModalOpen}
        handleClose={handleOperationStatusModalClose}
        header="Proceso de Operación"
      >
        <CPOOperationStatusModal
          props={{
            id: selectedCPO?.id,
            companyId,
            onSubmit: handleOperationStatusModalClose,
          }}
        />
      </LGModal>

      <Grid container direction="row" spacing={2} mb={2}>
        <Grid item>
          <Button
            onClick={() => setAddModalOpen(true)}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Agregar
          </Button>
        </Grid>
        <Grid item>
          <Button onClick={handleChange}>
            {showCompleted ? "Ocultar completados" : "Mostrar completados"}
          </Button>
        </Grid>
      </Grid>

      <div style={{ height: 600, width: "100%" }}>
        <AgGridReact
          ref={gridRef}
          context={{
            onEdit: handleEdit,
            onDelete: handleDelete,
            onViewShipments: handleViewShipments,
            onViewOperationStatus: handleViewOperationStatus,
            isSuperadmin,
          }}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          components={components}
          pagination={true}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={[10, 20, 50, 100]}
          animateRows={true}
          loading={loading}
          theme={themeBalham}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onPaginationChanged={onPaginationChanged}
          onCellValueChanged={handleCellValueChanged}
          onCellEditingStopped={handleCellEditingStopped}
          stopEditingWhenCellsLoseFocus={true}
          singleClickEdit={true}
          ensureDomOrder={true}
          suppressColumnVirtualisation={true}
          headerHeight={null}
        />
      </div>
    </Box>
  );
};

export default ClientPurchaseOrderCrud;
